import * as React from 'react';
import { Body2, IconChevronRight16, UIHeading4 } from '@cian/ui-kit';

import * as style from './style.css';

type TAiBannerFloatingPhase = 'hidden' | 'entering' | 'expanded' | 'exiting';

export interface IAiBannerFloatingExpandedProps {
  visible: boolean;
  title: string;
  subtitle: string;
  onOpen: () => void;
}

export const AiBannerFloatingExpanded = ({
  visible,
  title,
  subtitle,
  onOpen,
}: IAiBannerFloatingExpandedProps): React.JSX.Element | null => {
  const [phase, setPhase] = React.useState<TAiBannerFloatingPhase>(visible ? 'entering' : 'hidden');

  React.useEffect(() => {
    setPhase(currentPhase => {
      if (visible) {
        return currentPhase === 'hidden' || currentPhase === 'exiting' ? 'entering' : currentPhase;
      }

      if (
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return 'hidden';
      }

      return currentPhase === 'hidden' ? currentPhase : 'exiting';
    });
  }, [visible]);

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    setPhase(currentPhase => {
      if (currentPhase === 'entering') {
        return 'expanded';
      }

      return currentPhase === 'exiting' ? 'hidden' : currentPhase;
    });
  };

  if (phase === 'hidden') {
    return null;
  }

  return (
    <div
      className={`${style['container']} ${style[`container-${phase}`]}`}
      data-testid="ai-banner-floating-icon"
      onAnimationEnd={handleAnimationEnd}
      onClick={onOpen}
    >
      <div className={style['surface']} />
      <div className={style['layers']}>
        <div className={style['yellow-layer']} />
        <div className={style['orange-layer']} />
        <div className={style['blue-layer']}>
          <div className={style['star']} data-testid="ai-banner-floating-star" />
        </div>
      </div>
      <div className={style['content']}>
        <div className={style['text-wrapper-expanded']}>
          <UIHeading4>{title}</UIHeading4>
          <Body2>{subtitle}</Body2>
        </div>
        <div className={style['chevron']} data-testid="ai-banner-floating-chevron">
          <IconChevronRight16 color="icon-main-default" />
        </div>
      </div>
    </div>
  );
};
